import services from "../services";
import i18n from "./../i18n";
import dayjs from "dayjs";
import store from "../store";
import Utils from "../Utils";

const strict = true;

const getDefaultState = function () {
  return {
    unreadNotifications: [],
    numberOfUnreadNotifications: 0,
    notificationTypes: [],
    searchCriteriaResult: "",
    typeFilter: [],
    dateRange: {
      start: null,
      end: null
    },
    page: 0,
    size: 10,
    notifications: [],
    numberOfNotifications: 0,
    showNotificationPanel: false
  };
};

const state = getDefaultState();

const getters = {
  getUnreadNotifications: state => state.unreadNotifications,
  getNumberOfUnreadNotifications: state => state.numberOfUnreadNotifications,
  getNotificationTypes: state => state.notificationTypes,
  getSearchCriteriaResult: state => state.searchCriteriaResult,
  getTypeFilter: state => state.typeFilter,
  getDateRange: state => state.dateRange,
  getPage: state => state.page,
  getNotifications: state => state.notifications,
  getNumberOfNotifications: state => state.numberOfNotifications,
  getShowNotificationPanel: state => state.showNotificationPanel
};

const mutations = {
  RESET(state) {
    // acquire initial state
    const s = getDefaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  setNumberOfUnreadNotifications(state, num) {
    state.numberOfUnreadNotifications = num;
  },
  setUnreadNotifications(state, result) {
    state.unreadNotifications = result.notifications;
    state.numberOfUnreadNotifications = result.numberOfNotifications;
  },
  notificationTypesRetrieved(state, notificationTypes) {
    let notificationTypesLocalized = [];
    for (let notType of notificationTypes) {
      let type = "NOTIFICATION_".concat(notType);
      let localizedNotType = {
        id: notType,
        label: i18n ? i18n.t(type) : type
      };
      notificationTypesLocalized.push(localizedNotType);
    }
    notificationTypesLocalized.sort((a, b) => (a.label > b.label ? 1 : -1));
    state.notificationTypes = notificationTypesLocalized;
  },
  updateTypeFilterField(state, value) {
    state.typeFilter = value;
  },
  updateTypeFilterFieldWhenRemoved(state, index) {
    state.typeFilter.splice(index, 1);
  },
  updateDateOptionsFilterField(state, payload) {
    state.dateRange = payload.dr;
  },
  updatePage(state, page) {
    state.page = page;
  },
  setNotifications(state, result) {
    state.notifications = result.notifications;
    state.numberOfNotifications = result.numberOfNotifications;
  },
  markAsRead(state, notificationId) {
    let idx = state.notifications.findIndex(
      notification => notification.notificationId === notificationId
    );
    if (idx > -1) {
      state.notifications[idx].read = true;
      state.numberOfUnreadNotifications--;
    } else {
      idx = state.unreadNotifications.findIndex(
        notification => notification.notificationId === notificationId
      );
      if (idx > -1) {
        state.unreadNotifications[idx].read = true;
        state.numberOfUnreadNotifications--;
      }
    }
  },
  buildSearchCriteriaResult(state) {
    let strResult = "";
    if (state.typeFilter.length > 0) {
      if (strResult != "") {
        strResult = strResult + " & ";
      }
      let typeFilterLocalized = [];
      for (let tFilter of state.typeFilter) {
        typeFilterLocalized.push(
          i18n
            ? i18n.t("NOTIFICATION_".concat(tFilter))
            : "NOTIFICATION_".concat(sFilter)
        );
      }
      strResult = strResult + "'" + typeFilterLocalized.join(", ") + "'";
    }
    if (state.dateRange) {
      let start = state.dateRange.start;
      if (start) {
        if (strResult != "") {
          strResult = strResult + " & ";
        }
        let formattedStart = dayjs(start).format("DD/MM/YYYY");
        strResult = strResult + "'" + formattedStart + "'";
      }
      let end = state.dateRange.end;
      if (end) {
        if (strResult != "") {
          strResult = strResult + " & ";
        }
        let formattedEnd = dayjs(end).format("DD/MM/YYYY");
        strResult = strResult + "'" + formattedEnd + "'";
      }
    }
    state.searchCriteriaResult = strResult;
  },
  setShowNotificationPanel(state, value) {
    state.showNotificationPanel = value;
  }
};

const actions = {
  async getNotificationsUnreadCount({ commit }) {
    try {
      commit("loading/OPEN_LOADING_DIALOG", null, {
        root: true
      });
      let officeId = store.getters["hfManagement/officeId"];
      let numOfUnreadNotifications =
        await services.notificationsService.callGetNotificationsUnreadCount(
          officeId
        );
      commit("setNumberOfUnreadNotifications", numOfUnreadNotifications);
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
      return numOfUnreadNotifications;
    } catch (error) {
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
      let errorDialogDetails = {
        ...error
      };
      errorDialogDetails.type = Utils.errorTypes.STATUS;
      errorDialogDetails.dataName = "notifications";
      commit("error/SET_ERROR", errorDialogDetails, { root: true });
    }
  },
  async getUnreadNotifications({ commit }) {
    try {
      commit("loading/OPEN_LOADING_DIALOG", null, {
        root: true
      });
      let officeId = store.getters["hfManagement/officeId"];
      let payload = {
        officeId: officeId,
        readStatus: false,
        orderingType: "DESC"
      };
      let result = await services.notificationsService.callGetNotifications(
        payload
      );
      commit("setUnreadNotifications", result);
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
    } catch (error) {
      commit("loading/CLOSE_LOADING_DIALOG", null, {
        root: true
      });
      let errorDialogDetails = {
        ...error
      };
      commit("error/SET_ERROR", errorDialogDetails, { root: true });
    }
  },
  getNotificationTypes({ commit, rootGetters }) {
    let notificationTypes = ["APPOINTMENT", "BILL"];
    let userPermissions = rootGetters["auth/userPermissionList"];
    let userPermissionPageForBill = userPermissions.find(
      permission => permission.portalArea === "RENDICONTAZIONE"
    );
    let userPermissionPageForGestRich = userPermissions.find(
      permission => permission.portalArea === "GESTIONERICHIESTE"
    );
    let validBillPerm = userPermissionPageForBill
      ? userPermissionPageForBill.perm != "NOREAD"
      : false;
    let validGestRichPerm = userPermissionPageForGestRich
      ? userPermissionPageForGestRich.perm != "NOREAD"
      : false;
    if (!validBillPerm) {
      notificationTypes = notificationTypes.filter(type => type !== "BILL");
    }
    if (!validGestRichPerm) {
      notificationTypes = notificationTypes.filter(
        type => type !== "APPOINTMENT"
      );
    }
    commit("notificationTypesRetrieved", notificationTypes);
  },
  updateTypeFilterField({ commit }, typeFilter) {
    commit("updateTypeFilterField", typeFilter);
  },
  updateTypeFilterFieldAndReloadNotifications(
    { commit, dispatch },
    typeFilter
  ) {
    commit("updateTypeFilterField", typeFilter);
    return dispatch("reloadNotifications");
  },
  updateTypeFilterFieldWhenRemovedAndReloadNotifications(
    { state, commit, dispatch },
    value
  ) {
    const index = state.typeFilter.indexOf(value.id);
    if (index >= 0) {
      commit("updateTypeFilterFieldWhenRemoved", index);
    }
    return dispatch("reloadNotifications");
  },
  updateDateOptionsFilterField({ commit }, payload) {
    commit("updateDateOptionsFilterField", payload);
  },
  updateDateOptionsFilterFieldAndReloadNotifications(
    { commit, dispatch },
    payload
  ) {
    commit("updateDateOptionsFilterField", payload);
    return dispatch("reloadNotifications");
  },
  updatePageAndReloadNotifications({ commit, dispatch }, page) {
    commit("updatePage", page);
    return dispatch("reloadNotifications");
  },
  reloadNotifications({ state, dispatch }) {
    let dateFrom = state.dateRange
      ? state.dateRange.start
        ? state.dateRange.start
        : null
      : null;
    let dateTo = state.dateRange
      ? state.dateRange.end
        ? state.dateRange.end
        : null
      : null;
    let officeId = store.getters["hfManagement/officeId"];
    let payload = {
      officeId: officeId,
      size: state.size,
      orderingType: "DESC",
      page: state.page ? state.page - 1 : 0,
      type: state.typeFilter.join(","),
      fromDate: dateFrom ? dayjs(dateFrom).format("DD/MM/YYYY") : null,
      toDate: dateTo ? dayjs(dateTo).format("DD/MM/YYYY") : null
    };
    return dispatch("getNotifications", payload);
  },
  async getNotifications({ commit }, payload) {
    try {
      commit("loading/OPEN_LOADING_DIALOG", null, {
        root: true
      });
      let result = await services.notificationsService.callGetNotifications(
        payload
      );
      commit("setNotifications", result);
      commit("buildSearchCriteriaResult");
    } catch (error) {
      let errorDialogDetails = {
        ...error
      };
      commit("error/SET_ERROR", errorDialogDetails, { root: true });
    } finally {
      commit("loading/RESET", null, { root: true });
    }
  },
  async markAsRead({ commit }, notificationId) {
    try {
      let officeId = store.getters["hfManagement/officeId"];
      let payload = {
        officeId: officeId,
        notificationId: notificationId
      };
      await services.notificationsService.callMarkNotificationAsRead(payload);
      commit("markAsRead", notificationId);
    } catch (error) {
      let errorDialogDetails = {
        ...error
      };
      commit("error/SET_ERROR", errorDialogDetails, { root: true });
    }
  }
};

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
